// TODO remove this once we get aw-security types in the project
import { SearchRequest, SearchResponse, Security, SecurityID } from '@blk/awt-aladdinportfoliobuilderserver';
import { CancelToken } from 'axios';
import { chunk } from 'lodash';
import { AGraphWrappedType, unwrapAGraphResponse, wrapAGraphRequest } from '../../utils/AGraphWrapper';
import { conditionalParam, isAGraphEnabled } from './AGraphUtils';
import { apbApi } from './Base';
import { CHUNK_SIZE } from './Constants';

export const POST_LIST_SECURITIES_BY_CUSIP_PATH = '/api/v3/securities';
export const POST_RESOLVE_SECURITIES_PATH = 'api/v3/securities/_resolve';
export const POST_SEARCH_SECURITIES_PATH = 'api/v3/securities/_search';
export const GET_LIVE_SECURITIES_BY_CUSIP_PATH = '/api/v3/securities/live';

export async function postListSecuritiesByCusip(
  cusips: string[],
  date: string,
  pkg: string,
  cancelToken?: CancelToken
): Promise<Security[]> {
  const result = await apbApi.post<AGraphWrappedType<Security[], 'securities'>>(
    POST_LIST_SECURITIES_BY_CUSIP_PATH,
    wrapAGraphRequest(cusips, 'ids', isAGraphEnabled()),
    {
      params: { date, pkg },
      cancelToken,
    }
  );
  return unwrapAGraphResponse(result, 'securities').data;
}

export async function postResolveSecurities(securityIdentifiers: SecurityID[], date: string, pkg: string) {
  const response = await apbApi.post<AGraphWrappedType<Security[], 'securities'>>(
    POST_RESOLVE_SECURITIES_PATH,
    wrapAGraphRequest(securityIdentifiers, 'securityIds', isAGraphEnabled()),
    {
      params: { date, pkg },
    }
  );

  return unwrapAGraphResponse(response, 'securities');
}

export function postSearchSecurities(
  searchRequest: SearchRequest,
  date: string,
  pkg: string,
  cancelToken?: CancelToken
) {
  // If selects are provided, ensure currency is included so we can verify all securities' currencies are in the fxRates.
  const currencySelect = { name: 'currency', type: 'string' };
  const selects =
    searchRequest.selects.length && !searchRequest.selects.some((select) => select.name === 'currency')
      ? [...searchRequest.selects, currencySelect]
      : searchRequest.selects;

  const validatedSearchRequest = {
    ...searchRequest,
    selects,
  };

  return apbApi.post<SearchResponse>(POST_SEARCH_SECURITIES_PATH, validatedSearchRequest, {
    params: { date, pkg },
    cancelToken,
  });
}

/**
 * This route fetches security objects from the alternate LIVE AWSecurity Repo, which preserves data for obsolete
 * securities that no longer belong to the daily AWSecurity universe
 */
export async function getLiveSecuritiesByCusip(
  cusips: string[],
  date: string,
  cancelToken?: CancelToken
): Promise<Security[]> {
  /**
   * This is a GET request which could be too long in some browsers if it is over 2048 characters, so we chunk
   * the request in 150 CUSIP chunks which will ensure we're always below the threshold, given CUSIPs are always
   * 9 characters long
   */
  const chunkedResult = await Promise.all(
    chunk(cusips, CHUNK_SIZE).map((cusipsChunk) =>
      apbApi.get<AGraphWrappedType<Security[], 'securities'>>(GET_LIVE_SECURITIES_BY_CUSIP_PATH, {
        params: {
          date: conditionalParam(GET_LIVE_SECURITIES_BY_CUSIP_PATH, 'date', date),
          ids: cusipsChunk.join(','),
        },
        cancelToken,
      })
    )
  );

  return chunkedResult.reduce<Security[]>((memo, next) => {
    const unwrapped = unwrapAGraphResponse(next, 'securities').data;
    memo.push.apply(memo, unwrapped);
    return memo;
  }, []);
}
